import { IOption } from '@user/components/Filters/typings'
// Imported by path, not via the Filters barrel: the barrel pulls the whole
// filter component tree back into the store and creates an import cycle.
import { resolveOptionLabels } from '@user/components/Filters/utils/resolveOptionLabels'
import {
  KNOCK_STATUS_FILTER_NAME,
  knockStatusesToOptions,
} from '../knockStatus/helpers'
import { IKnockStatus } from '../knockStatus/typings'
import { IUserPreferenceFilter } from './typings'

/** Shape produced by `serializeFilters` — only the parts we need to rewrite. */
type SerializedFilterSet = {
  orderBy?: string
  filters?: Record<string, { value?: { options?: IOption[] } }>
}

const parsePref = (pref: IUserPreferenceFilter): SerializedFilterSet | null => {
  try {
    return JSON.parse(pref.filter) as SerializedFilterSet
  } catch {
    return null
  }
}

const readOptions = (parsed: SerializedFilterSet | null): IOption[] =>
  parsed?.filters?.[KNOCK_STATUS_FILTER_NAME]?.value?.options ?? []

export const prefsHaveKnockStatusFilter = (
  prefs: IUserPreferenceFilter[],
): boolean => prefs.some((pref) => readOptions(parsePref(pref)).length > 0)

/**
 * A knock-status selection is persisted as a `{ value, label }` snapshot inside
 * the saved filter preference, so it outlives both the tenant feature that
 * exposes it and the knock status it points at. Reconcile it as the preference
 * is loaded — before any page turns the filter set into an OData query or a
 * filter chip.
 *
 * Pass `null` for `knockStatuses` when the feature is disabled, to drop the
 * filter entirely; otherwise deleted statuses are dropped and renamed ones are
 * relabelled.
 */
export function applyKnockStatusRulesToPref(
  pref: IUserPreferenceFilter,
  knockStatuses: IKnockStatus[] | null,
): IUserPreferenceFilter {
  const parsed = parsePref(pref)
  const options = readOptions(parsed)

  if (!parsed || options.length === 0) return pref

  let next: IOption[] = []

  if (knockStatuses !== null) {
    // An empty catalog means the fetch failed — leave the selection alone
    // rather than silently wiping the user's filter.
    if (knockStatuses.length === 0) return pref

    const items = knockStatusesToOptions(knockStatuses)
    const known = new Set(items.map((item) => String(item.value)))

    next = resolveOptionLabels(options, items).filter((option) =>
      known.has(String(option.value)),
    )
  }

  if (next.length === options.length && next.every((o, i) => o === options[i]))
    return pref

  const filters = { ...parsed.filters }

  if (next.length === 0) delete filters[KNOCK_STATUS_FILTER_NAME]
  else
    filters[KNOCK_STATUS_FILTER_NAME] = {
      ...filters[KNOCK_STATUS_FILTER_NAME],
      value: {
        ...filters[KNOCK_STATUS_FILTER_NAME]?.value,
        options: next,
      },
    }

  return { ...pref, filter: JSON.stringify({ ...parsed, filters }) }
}
